import type { HostedEvent } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadHostedEvents } from "../../core/ops/hosted-event-ops";
import { SHOWS_FAILED_TO_LOAD, showDates, splitShows } from "../../core/primitives/hosted-event";
import { failedLoad, loaded, loading } from "../../core/primitives/load-status";
import { can } from "../../core/primitives/store-role";
import { CapabilityRequestController } from "../../sections/capability-request/capability-request-controller";
import { EventsActions, EventsState } from "./events-model";
import { VendorShowsSectionController } from "./sections/vendor-shows/vendor-shows-section-controller";

export type EventsDeps = Pick<
  AppDeps,
  | "client"
  | "authHandler"
  | "storeRepository"
  | "locationsRepository"
  | "capabilitiesRepository"
  | "stripePayments"
  | "shareLinks"
>;

export interface EventsRouting {
  toNew(): void;
  toShow(eventId: string): void;
}

/**
 * The shop's shows. Every member reads them; a Manager or an Owner opens one, and hosts new ones
 * once hosting is on. Below them, other shops' shows, where a Manager or an Owner takes a table.
 */
export class EventsController extends SubtreeController implements EventsActions {
  readonly state = new EventsState();
  readonly vendorShows: VendorShowsSectionController;
  /** Asking HobbyScan to switch hosting on. */
  readonly hosting: CapabilityRequestController;
  #storeId: string | null = null;
  #events: HostedEvent[] = [];

  readonly now: () => Date;

  constructor(
    readonly deps: EventsDeps,
    readonly routing: EventsRouting,
    { now = () => new Date() }: { now?: () => Date } = {},
  ) {
    super();
    this.subtree.put(EventsState, this.state);
    this.subtree.put(EventsActions, this);
    this.sync(() => {
      const listed = this.state.upcoming.value.filter((e) => e.isPublished && !e.isBlocked && !e.cancelledAt);
      this.state.shares.value = new Map(listed.map((e) => [e.id, deps.shareLinks.show(e, showDates(e))]));
    }, [this.state.upcoming]);
    this.now = now;
    this.vendorShows = this.own(new VendorShowsSectionController(this.subtree, deps, { now }));
    this.hosting = this.own(new CapabilityRequestController(this.subtree, deps, "HOST_EVENTS"));
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.mayManage.value = store ? can(store.myRole, "hostEvents") : false;
      const storeId = store?.id ?? null;
      if (storeId === this.#storeId) return;
      this.#storeId = storeId;
      this.#events = [];
      this.#show();
      if (storeId) {
        this.state.status.value = loading;
        void this.#load(storeId);
      }
    }, [deps.storeRepository]);
  }

  create = () => {
    this.routing.toNew();
  };

  open = (eventId: string) => {
    this.routing.toShow(eventId);
  };

  retry = () => {
    const storeId = this.#storeId;
    if (!storeId || this.state.status.value.kind === "loading") return;
    this.state.status.value = loading;
    void this.#load(storeId);
  };

  async #load(storeId: string): Promise<void> {
    let r: Awaited<ReturnType<typeof loadHostedEvents>>;
    try {
      r = await loadHostedEvents({ ...this.deps, storeId });
    } catch (err) {
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.status.value = { kind: "error", message: SHOWS_FAILED_TO_LOAD };
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.canHost.value = r.value.canHost;
    this.#events = r.value.events;
    this.#show();
    this.state.status.value = loaded;
  }

  #show(): void {
    const { upcoming, past } = splitShows(this.#events, this.now());
    this.state.upcoming.value = upcoming;
    this.state.past.value = past;
  }
}
